/**
 * Auto Place.
 *
 * Two modes, and the distinction is the whole design. **Improve** starts from
 * where the parts already are and does not throw away work somebody did by
 * hand; **place from scratch** solves for minimum wirelength and legalises
 * from there, which is what an unplaced board wants and what a placed one
 * emphatically does not.
 *
 * Nothing is applied until it is accepted. The run produces a list of moves and
 * a before-and-after; pressing Apply lands the lot as one undoable operation.
 */

import type { PlaceResult } from '@sircad/core/route/place.ts';
import type { CongestionReport } from '@sircad/core/route/congestion.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface PlaceOptionsChosen {
  keepPositions: boolean;
  twoSided: boolean;
  allow45: boolean;
  sweeps: number;
  seed: number;
}

export interface PlaceDialogOptions {
  /** How many parts are movable, and how many are pinned. */
  movable: number;
  fixed: number;
  /** Parts sitting off the board — the sign that this board is unplaced. */
  offBoard: number;
  /** Tracks already laid. They do not follow the parts. */
  routed: number;
  formatLength(inches: number): string;
  /** Run a placement and hand back what it found. Does not change the board. */
  run(chosen: PlaceOptionsChosen): Promise<{
    result: PlaceResult;
    before: CongestionReport;
    after: CongestionReport;
  }>;
  /** Land the moves as one undoable operation. */
  apply(result: PlaceResult): void;
}

export function showPlaceDialog(opts: PlaceDialogOptions): void {
  openDialog({
    title: 'Auto Place',
    wide: true,
    build(body, handle) {
      const form = document.createElement('div');
      form.className = 'place-form';

      const line = (label: string, control: HTMLElement, why?: string): void => {
        const row = document.createElement('label');
        row.className = 'place-row';
        const text = document.createElement('span');
        text.textContent = label;
        if (why) text.title = why;
        row.append(control, text);
        form.appendChild(row);
      };

      const check = (checked: boolean): HTMLInputElement => {
        const c = document.createElement('input');
        c.type = 'checkbox';
        c.checked = checked;
        return c;
      };

      // A board with parts parked beside it has not been placed yet, so that is
      // the one case where starting from scratch is the obvious default.
      const unplaced = opts.offBoard > opts.movable * 0.25;
      const scratch = check(unplaced);
      line('Place from scratch', scratch,
        'Solve for shortest wiring and legalise from there. Off, the existing '
        + 'placement is the starting point and is only improved.');

      const twoSided = check(false);
      line('Allow parts on both sides', twoSided,
        'A through-hole part can never flip; a one-sided board is the cheaper '
        + 'board to assemble.');

      const allow45 = check(true);
      line('Allow 45° orientations', allow45,
        'Multiples of 90° are always preferred; this permits the odd 45s.');

      const effort = document.createElement('select');
      for (const [label, value] of [['Quick', '15'], ['Normal', '30'], ['Thorough', '60']] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        effort.appendChild(o);
      }
      effort.value = '30';
      line('Effort', effort, 'Annealing sweeps. More is slower and better.');

      const seed = document.createElement('input');
      seed.type = 'number';
      seed.value = '1';
      seed.min = '1';
      seed.style.width = '5em';
      line('Seed', seed,
        'The same board and the same seed always place identically. Change it '
        + 'to try a different arrangement.');

      body.appendChild(form);

      const note = document.createElement('p');
      note.className = 'dfm-note';
      note.textContent = `${opts.movable} parts can move`
        + (opts.fixed > 0 ? `, ${opts.fixed} are fixed` : '')
        + (opts.offBoard > 0 ? `, ${opts.offBoard} are off the board` : '')
        + '.';
      body.appendChild(note);

      if (opts.routed > 0) {
        // Copper does not follow the parts, and on a routed board that is the
        // first thing you will see: every track left where it was, joining
        // pads that have moved. Better said here than discovered afterwards.
        const warn = document.createElement('p');
        warn.className = 'dfm-note';
        warn.textContent = `This board already has ${opts.routed} tracks on it. `
          + 'Moving parts does not move copper, so the existing routing will be '
          + 'wrong afterwards and has to be ripped up and laid again.';
        body.appendChild(warn);
      }

      const out = document.createElement('div');
      out.className = 'place-result';
      body.appendChild(out);

      const bar = buttons(body);
      let found: PlaceResult | null = null;

      const applyBtn = button(bar, 'Apply', () => {
        if (!found) return;
        opts.apply(found);
        handle.close();
      });
      applyBtn.classList.add('primary');
      applyBtn.disabled = true;

      const runBtn = button(bar, 'Run', () => {
        runBtn.disabled = true;
        applyBtn.disabled = true;
        out.textContent = 'Placing…';

        void opts.run({
          keepPositions: !scratch.checked,
          twoSided: twoSided.checked,
          allow45: allow45.checked,
          sweeps: Number(effort.value) || 30,
          seed: Math.max(1, Math.round(Number(seed.value) || 1)),
        }).then(({ result, before, after }) => {
          found = result;
          runBtn.disabled = false;
          applyBtn.disabled = result.moves.length === 0;
          out.textContent = '';

          const table = document.createElement('dl');
          table.className = 'density-stats';
          const row = (label: string, was: string, now: string): void => {
            const dt = document.createElement('dt');
            dt.textContent = label;
            const dd = document.createElement('dd');
            dd.textContent = `${was} → ${now}`;
            table.append(dt, dd);
          };
          row('Connection length',
            opts.formatLength(before.mstLength), opts.formatLength(after.mstLength));
          row('Crossings', String(before.crossings), String(after.crossings));
          row('Overflow', before.overflow.toFixed(0), after.overflow.toFixed(0));
          row('Layers needed', String(before.layersNeeded), String(after.layersNeeded));
          row('Parts moved', '', String(result.moves.length));
          out.appendChild(table);

          if (result.unplaceable.length > 0) {
            const p = document.createElement('p');
            p.className = 'dfm-note';
            p.textContent = `No room found for ${result.unplaceable.length}, left where `
              + `they were: ${result.unplaceable.slice(0, 12).join(', ')}`;
            out.appendChild(p);
          }

          const anneal = result.stages.find((s) => s.stage === 'anneal');
          const timing = document.createElement('p');
          timing.className = 'dfm-note';
          timing.textContent = result.stages
            .filter((s) => s.ms > 0)
            .map((s) => `${s.stage} ${(s.ms / 1000).toFixed(1)}s`)
            .join(' · ')
            + (anneal?.accepted ? ` · ${anneal.accepted} moves taken` : '');
          out.appendChild(timing);
        }).catch((err: unknown) => {
          runBtn.disabled = false;
          out.textContent = `Placement failed: ${String(err)}`;
        });
      });

      button(bar, 'Close', () => handle.close());
    },
  });
}
